import { baseApi } from './base-api';
import { getIdentityGeneration } from '../utils/identity-scope';
import { API_ENDPOINTS } from '../utils/constants';

enum ApiKeyType {
  Public = 'public',
  Test = 'test',
}

interface ApiKey {
  id: string;
  uid: string;
  org_id: string;
  application: string;
  domains: string[];
  created: string;
  updated: string;
  type: string;
  value: string;
}

export interface ApiKeyResponse {
  api_keys: ApiKey[];
}

export interface CreateApiKeysArgs {
  types: Array<'public' | 'secret' | 'test'>;
}

interface Response {
  response: ApiKey[];
  type: string;
}

// --- New Interfaces for Validation ---
interface ValidateApiKeysArgs {
  public_key: string;
  test_key: string;
}

interface ValidateApiResponse {
  type: 'success' | 'error';
  response?: string;
  reason?: string;
}

const apiKeysApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createApiKeys: builder.mutation<{ type: string; response: ApiKey[] }, CreateApiKeysArgs>({
      query: (data) => ({
        url: '/api-keys',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ApiKey', 'Organization'],
    }),
    getApiKeys: builder.query<Response, void>({
      query: () => {
        const url = new URL('/v1/api-keys', API_ENDPOINTS.KARLA_FLASK_API);
        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      providesTags: ['ApiKey'],
    }),
    getApiKeyById: builder.query<ApiKey, string>({
      query: (id) => {
        const url = new URL(`/v1/api-keys/${id}`, API_ENDPOINTS.KARLA_FLASK_API);
        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      providesTags: (result, error, id) => [{ type: 'ApiKey', id }],
    }),
    validateApiKeys: builder.query<ValidateApiResponse, ValidateApiKeysArgs>({
      query: ({ public_key, test_key }) => {
        // Assumes the validation endpoint is at /v1/api-keys/validate
        const url = new URL('/v1/api-keys/validate', API_ENDPOINTS.KARLA_FLASK_API);
        // Add keys as URL search parameters
        url.searchParams.append('public_key', public_key);
        url.searchParams.append('test_key', test_key);

        return {
          url: url.toString(),
          method: 'GET',
        };
      },
    }),
    updateApiKey: builder.mutation<ApiKey, { id: string; updatedData: Partial<ApiKey> }>({
      query: ({ id, updatedData }) => ({
        url: `api-keys/${id}`,
        method: 'PATCH',
        body: updatedData,
      }),
      transformResponse: (response, meta, arg) =>
        ({
          id: arg.id,
          ...arg.updatedData,
        }) as ApiKey,
      onQueryStarted: async ({ id, updatedData }, { dispatch, queryFulfilled }) => {
        const generation = getIdentityGeneration();
        const patchResult = dispatch(
          baseApi.util.updateQueryData('getApiKeyById', id, (draft) => {
            Object.assign(draft, updatedData);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          if (generation === getIdentityGeneration()) patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'ApiKey', id }],
    }),
  }),
});

export const { useGetApiKeyByIdQuery, useGetApiKeysQuery, useUpdateApiKeyMutation, useCreateApiKeysMutation, useValidateApiKeysQuery } = apiKeysApi;

export { apiKeysApi };
